import { z } from 'zod';
import type { BaseRecord } from './sync';

/**
 * Prospects : le superviseur (ou l'administrateur) saisit ou importe des contacts — nom, téléphone, commentaire —
 * et les attribue aux commerciaux. Le commercial appelle, puis enregistre chaque contact avec un résultat et un
 * commentaire (hors ligne possible). « Le client a validé » → le commercial crée le client (fiche pré-remplie),
 * rattaché au prospect. L'historique des contacts n'est jamais modifié.
 */

export const PROSPECT_OUTCOMES = ['validated', 'callback', 'no_answer', 'already_equipped', 'not_interested', 'other'] as const;
export type ProspectOutcome = (typeof PROSPECT_OUTCOMES)[number];
export const PROSPECT_OUTCOME_LABELS: Record<ProspectOutcome, string> = {
  validated: 'Le client a validé',
  callback: 'Revient vers nous / à rappeler',
  no_answer: 'Injoignable / messagerie',
  already_equipped: 'Déjà équipé',
  not_interested: 'Pas intéressé',
  other: 'Autre',
};

export const PROSPECT_STATUSES = ['to_contact', 'callback', 'won', 'closed', 'archived'] as const;
export type ProspectStatus = (typeof PROSPECT_STATUSES)[number];
export const PROSPECT_STATUS_LABELS: Record<ProspectStatus, string> = {
  to_contact: 'À contacter',
  callback: 'À rappeler',
  won: 'Validé',
  closed: 'Clôturé',
  archived: 'Archivé',
};
export const PROSPECT_STATUS_TONES: Record<ProspectStatus, 'info' | 'warning' | 'success' | 'neutral'> = {
  to_contact: 'info',
  callback: 'warning',
  won: 'success',
  closed: 'neutral',
  archived: 'neutral',
};

/** Status after a contact with this outcome. */
export function statusAfter(outcome: ProspectOutcome): ProspectStatus {
  if (outcome === 'validated') return 'won';
  if (outcome === 'callback' || outcome === 'no_answer') return 'callback';
  return 'closed';
}

export interface ProspectContact {
  id: string;
  outcome: ProspectOutcome;
  comment: string | null;
  callbackOn: string | null;
  at: string;
  byName: string;
}

export interface ProspectRecord extends BaseRecord {
  territoryId: string;
  name: string;
  phone: string;
  email: string | null;
  city: string | null;
  source: string | null;
  /** consigne / informations du superviseur */
  comment: string | null;
  status: ProspectStatus;
  assignedToId: string | null;
  assignedToName: string | null;
  assignedAt: string | null;
  assignedByName: string | null;
  callbackOn: string | null;
  lastContactAt: string | null;
  lastOutcome: ProspectOutcome | null;
  /** most recent first */
  contacts: ProspectContact[];
  clientId: string | null;
  createdAt: string;
  createdByName: string;
}

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide');
const phone = z
  .string()
  .trim()
  .min(6, 'Téléphone obligatoire')
  .max(30)
  .refine((v) => (v.match(/\d/g) ?? []).length >= 6, 'Téléphone invalide');

export const prospectInput = z.object({
  id: z.string().uuid(),
  territoryId: z.string().uuid(),
  name: z.string().trim().min(1, 'Nom obligatoire').max(200),
  phone,
  email: z.string().trim().email('E-mail invalide').max(200).nullish().or(z.literal('')).transform((v) => v || null),
  city: z.string().trim().max(120).nullish().transform((v) => v || null),
  source: z.string().trim().max(120).nullish().transform((v) => v || null),
  comment: z.string().trim().max(2000).nullish().transform((v) => v || null),
  assignedToId: z.string().uuid().nullish().transform((v) => v ?? null),
});
export type ProspectInput = z.input<typeof prospectInput>;

export const prospectPatch = z
  .object({
    name: z.string().trim().min(1).max(200),
    phone,
    email: z.string().trim().email().max(200).nullable(),
    city: z.string().trim().max(120).nullable(),
    source: z.string().trim().max(120).nullable(),
    comment: z.string().trim().max(2000).nullable(),
    archived: z.boolean(),
  })
  .partial();

export const prospectAssignInput = z.object({ ids: z.array(z.string().uuid()).min(1).max(1000), userId: z.string().uuid().nullable() });

export const prospectContactInput = z
  .object({
    outcome: z.enum(PROSPECT_OUTCOMES),
    comment: z.string().trim().max(2000).nullish().transform((v) => v || null),
    callbackOn: day.nullish().transform((v) => v ?? null),
    at: z.string().datetime().optional(),
  })
  .superRefine((v, c) => {
    if (v.outcome === 'other' && !v.comment) c.addIssue({ code: 'custom', path: ['comment'], message: 'Précisez en commentaire' });
    if (v.callbackOn && statusAfter(v.outcome) !== 'callback') c.addIssue({ code: 'custom', path: ['callbackOn'], message: 'Date de rappel seulement pour « à rappeler » ou « injoignable »' });
  });
export type ProspectContactInput = z.input<typeof prospectContactInput>;

export const prospectLinkClientInput = z.object({ clientId: z.string().uuid() });

/**
 * Import (tableur copié-collé ou CSV) : une ligne par contact, colonnes nom ; téléphone ; ville ; commentaire
 * (séparateur « ; », « , » ou tabulation). Une ligne d'en-tête est ignorée.
 */
export function parseProspectLines(text: string): { rows: { name: string; phone: string; city: string | null; comment: string | null }[]; errors: { line: number; message: string }[] } {
  const rows: { name: string; phone: string; city: string | null; comment: string | null }[] = [];
  const errors: { line: number; message: string }[] = [];
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) return;
    const sep = line.includes('\t') ? '\t' : line.includes(';') ? ';' : ',';
    const cells = line.split(sep).map((c) => c.trim().replace(/^"(.*)"$/, '$1'));
    const [name = '', tel = '', city = '', ...rest] = cells;
    if (i === 0 && /nom|name/i.test(name) && /t[ée]l|phone/i.test(tel)) return;
    if (!name) return errors.push({ line: i + 1, message: 'Nom manquant' });
    if ((tel.match(/\d/g) ?? []).length < 6) return errors.push({ line: i + 1, message: 'Téléphone manquant ou invalide' });
    rows.push({ name, phone: tel, city: city || null, comment: rest.join(sep === ',' ? ', ' : ' ').trim() || null });
  });
  return { rows, errors };
}

/** Digits only, for duplicates and `tel:` links (keeps a leading +). */
export const normalizePhone = (p: string) => p.trim().replace(/(?!^\+)[^\d]/g, '');

/** The commercial's working list: callbacks due first, then new ones, then the rest. */
export function prospectsToWork<T extends Pick<ProspectRecord, 'status' | 'callbackOn' | 'assignedAt' | 'createdAt'>>(list: T[], today: string): T[] {
  const rank = (p: T) => (p.status === 'callback' && p.callbackOn && p.callbackOn <= today ? 0 : p.status === 'to_contact' ? 1 : p.status === 'callback' ? 2 : 3);
  return [...list].sort((a, b) => rank(a) - rank(b) || (a.callbackOn ?? '').localeCompare(b.callbackOn ?? '') || (a.assignedAt ?? a.createdAt).localeCompare(b.assignedAt ?? b.createdAt));
}

/** Client form pre-filled from a prospect (« M. Jean Karu » → civility M, first name Jean, last name Karu). */
export function prospectToClient(p: Pick<ProspectRecord, 'name' | 'phone' | 'email' | 'city'>) {
  let rest = p.name.trim();
  let civility: 'M' | 'MME' | null = null;
  const m = /^(m\.|mr\.?|monsieur|mme\.?|madame|mlle\.?)\s+/i.exec(rest);
  if (m) {
    civility = /^m(\.|r|onsieur)/i.test(m[1]!) && !/^mme|^mlle/i.test(m[1]!) ? 'M' : 'MME';
    rest = rest.slice(m[0].length).trim();
  }
  const words = rest.split(/\s+/);
  const firstName = words.length > 1 ? words[0]! : '';
  const lastName = words.length > 1 ? words.slice(1).join(' ') : rest;
  return { kind: 'individual' as const, civility, firstName, lastName, phone: p.phone, email: p.email ?? undefined, city: p.city ?? undefined };
}
